import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import AnnouncementCard from '../../components/shared/AnnouncementCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useClientPendingInvoices } from '../../services/clientInvoiceService';
import { 
 User, 
 Mail, 
 Phone, 
 Key, 
 Shield, 
 CheckCircle, 
 Edit2,
 Copy,
 Eye,
 EyeOff,
 FileText,
 ArrowRight,
 Calendar,
} from 'lucide-react';

const ClientDashboard = () => {
 const { user } = useAuth();
 const navigate = useNavigate();
 const [showPassword, setShowPassword] = useState(false);
 const [copied, setCopied] = useState(false);

 // Fetch announcements
 const { data: announcementsData } = useAnnouncements();
 const announcements = announcementsData?.data || [];

 const { data: pendingInvoicesRes } = useClientPendingInvoices();
 const pendingInvoiceCount = pendingInvoicesRes?.data?.count ?? 0;

 const copyToClipboard = (text) => {
 navigator.clipboard.writeText(text);
 setCopied(true);
 setTimeout(() => setCopied(false), 2000);
 };

 const accountInfo = [
 {
 label: 'Username',
 value: user?.name || 'Client',
 icon: User,
 copyable: false
 },
 {
 label: 'Email',
 value: user?.email || 'N/A',
 icon: Mail,
 copyable: true
 },
 {
 label: 'Phone number',
 value: user?.phone || 'N/A',
 icon: Phone,
 copyable: true
 },
 {
 label: 'User ID',
 value: user?.id?.substring(0, 8) || 'N/A',
 icon: Shield,
 copyable: true,
 isMono: true
 }
 ];

 return (
 <ClientPageLayout
 title="My Account"
 subtitle="Review your profile, contact details, and account security at a glance."
 guideKey="account"
 >
 <div className="space-y-5">
 {/* Announcements */}
 {announcements.length > 0 && (
 <div>
 {announcements.map((announcement) => (
 <AnnouncementCard
 key={announcement.id}
 announcement={announcement}
 />
 ))}
 </div>
 )}

 {pendingInvoiceCount > 0 && (
 <div className="rounded-lg border border-amber-200/80 bg-amber-50 p-4 sm:p-5">
 <div className="flex flex-col sm:flex-row sm:items-start gap-4">
 <div className="flex items-start gap-3 flex-1 min-w-0">
 <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
 <FileText className="w-5 h-5 text-amber-800" />
 </div>
 <div>
 <h3 className="text-sm sm:text-base font-bold text-amber-950">
 Pending invoice{pendingInvoiceCount > 1 ? 's' : ''}
 </h3>
 <p className="text-sm text-amber-900/90 mt-1">
 You have {pendingInvoiceCount} invoice
 {pendingInvoiceCount > 1 ? 's' : ''} awaiting your attention from a vendor.
 Open My Bookings to view the linked booking and download each invoice.
 </p>
 </div>
 </div>
 <Link
 to="/bookings"
 className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-900 text-white text-sm font-medium rounded-md hover:bg-amber-950 transition-colors whitespace-nowrap"
 >
 View bookings
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 )}

 {/* Profile Header Card */}
 <div className="product-panel p-5 sm:p-6">
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
 {/* Avatar */}
 <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
 <span className="text-white font-bold text-2xl sm:text-3xl">
 {user?.name ? user.name.charAt(0).toUpperCase() : 'C'}
 </span>
 </div>
 
 {/* Profile Info */}
 <div className="flex-1 min-w-0">
 <h2 className="text-xl sm:text-2xl font-bold text-ink mb-2">
 {user?.name || 'Client'}
 </h2>
 <p className="text-sm sm:text-base text-ink-secondary mb-3">
 {user?.email || 'No email provided'}
 </p>
 <div className="flex items-center space-x-2">
 {user?.is_active ? (
 <span className="inline-flex items-center px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs sm:text-sm font-medium">
 <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5" />
 Active Account
 </span>
 ) : (
 <span className="inline-flex items-center px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
 Inactive Account
 </span>
 )}
 </div>
 </div>

 {/* Edit Button */}
 <button 
 onClick={() => navigate('/settings')}
 className="flex items-center space-x-2 px-4 py-2 border border-border rounded-md text-ink hover:bg-surface-muted transition-colors active:scale-95"
 >
 <Edit2 className="w-4 h-4" />
 <span className="text-sm font-medium">Edit Profile</span>
 </button>
 </div>
 </div>

 {/* Account Information Card */}
 <div className="product-panel p-4 sm:p-5">
 <h3 className="text-lg sm:text-xl font-bold text-ink mb-4 sm:mb-6">Account Information</h3>
 <div className="space-y-4">
 {accountInfo.map((info, index) => {
 const Icon = info.icon;
 return (
 <div
 key={index}
 className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 border-b border-border last:border-b-0 gap-2 sm:gap-0"
 >
 <div className="flex items-center space-x-3 flex-1 min-w-0">
 <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
 <Icon className="w-5 h-5 text-ink" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-xs sm:text-sm text-ink-secondary font-medium mb-1">{info.label}</p>
 <p className={`text-sm sm:text-base text-ink font-semibold truncate ${info.isMono ? 'font-mono' : ''}`}>
 {info.value}
 </p>
 </div>
 </div>
 {info.copyable && info.value !== 'N/A' && (
 <button
 onClick={() => copyToClipboard(info.value)}
 className="flex items-center space-x-1 px-3 py-1.5 text-xs sm:text-sm text-ink-secondary hover:text-ink border border-border rounded-md hover:border-ink transition-colors"
 title="Copy to clipboard"
 >
 <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
 <span>{copied ? 'Copied!' : 'Copy'}</span>
 </button>
 )}
 </div>
 );
 })}
 </div>
 </div>

 {/* Security Card */}
 <div className="product-panel p-4 sm:p-5">
 <div className="flex items-center space-x-3 mb-4 sm:mb-6">
 <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
 <Key className="w-5 h-5 text-ink" />
 </div>
 <h3 className="text-lg sm:text-xl font-bold text-ink">Security</h3>
 </div>
 
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 sm:py-4 gap-3 sm:gap-0">
 <div className="flex-1">
 <p className="text-xs sm:text-sm text-ink-secondary font-medium mb-1">Password</p>
 <div className="flex items-center space-x-2">
 <span className="text-sm sm:text-base text-ink font-mono">
 {showPassword ? '••••••••' : '••••••••'}
 </span>
 <button
 onClick={() => setShowPassword(!showPassword)}
 className="p-1 text-gray-400 hover:text-ink transition-colors"
 title={showPassword ? 'Hide password' : 'Show password'}
 >
 {showPassword ? (
 <EyeOff className="w-4 h-4" />
 ) : (
 <Eye className="w-4 h-4" />
 )}
 </button>
 </div>
 </div>
 <button 
 onClick={() => navigate('/settings')}
 className="flex items-center space-x-2 px-4 py-2 border border-border rounded-md text-ink hover:bg-surface-muted transition-colors active:scale-95"
 >
 <Key className="w-4 h-4" />
 <span className="text-sm font-medium">Change Password</span>
 </button>
 </div>
 </div>

 {/* Quick Actions Card */}
 <div className="product-panel p-4 sm:p-5">
 <h3 className="text-lg sm:text-xl font-bold text-ink mb-4 sm:mb-6">Quick Actions</h3>
 <div className="grid grid-cols-1 gap-3 sm:gap-4">
 <Link
 to="/bookings"
 className="group flex items-center gap-3 p-4 rounded-lg border border-border hover:border-border hover:bg-surface-muted/50 transition-colors"
 >
 <div className="w-11 h-11 bg-surface-muted border border-border rounded-xl flex items-center justify-center flex-shrink-0">
 <Calendar className="w-5 h-5 text-ink" />
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-sm font-semibold text-ink">My Bookings</p>
 <p className="text-xs text-ink-secondary">View events and invoices</p>
 </div>
 <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-ink flex-shrink-0" />
 </Link>
 </div>
 </div>
 </div>
 </ClientPageLayout>
 );
};

export default ClientDashboard;
